(ns practitest.frontend.components.view-editor.basic-builder
  "Basic-mode UI for the view editor. Walks AST, emits PTQL."
  (:require
    [clojure.string :as str]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.format-utils :as fu]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.selector2 :as selector2]
    [practitest.frontend.components.view-editor.events :as-alias events]
    [practitest.frontend.components.view-editor.subs :as-alias view-editor.subs]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.components-provider :refer [application-provider]]
    [practitest.frontend.pt-ts-bridge :as pt-ts-bridge]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]))

(def ^:private MAX-DEPTH 3)

(def ^:private comparison-ops
  ;; AST ops that map cleanly to a single basic-mode comparison row
  ;; NOTE: there is no "is-not" anymore, inequality is ["not", ["is", ...]]
  #{"is" "contains" "starts-with"
    "gt" "gte" "lt" "lte"
    "in-last" "empty" "not-empty"})

(def ^:private negatable-ops
  ;; Comparisons that have a dedicated NOT-form in PTQL
  ;; Surfaced in basic UI as "is not", "does not contain", "does not start with"
  #{"is" "contains" "starts-with" "in-last"})

(def ^:private keywords
  #{"AND" "OR" "NOT" "IS" "EMPTY" "IN" "LAST" "LINKED" "TO" "WHERE"
    "STARTS" "WITH" "CONTAINS" "CURRENT_USER" "OTHER" "GROUP" "VIEW" "COUNT"})

;; ---- shape predicates ----

(defn group? [ast]
  (and (vector? ast)
       (#{"and" "or"} (first ast))))

(defn comparison? [ast]
  (and (vector? ast)
       (comparison-ops (first ast))))

(defn- linked-to? [ast]
  (and (vector? ast)
       (= "exists-related" (first ast))))

(defn- count-node? [ast]
  (and (vector? ast)
       (= "count" (first ast))))

(defn not-node? [ast]
  (and (vector? ast)
       (= "not" (first ast))))

(defn negated-comparison? [ast]
  (and (not-node? ast)
       (vector? (second ast))
       (negatable-ops (first (second ast)))))

(defn- negated-linked-to? [ast]
  (and (not-node? ast)
       (linked-to? (second ast))))

(defn- cross-filter? [ast]
  (and (vector? ast)
       (= "matches" (first ast))))

(defn- negated-cross-filter? [ast]
  (and (not-node? ast)
       (cross-filter? (second ast))))

;; ---- view-fits-basic? ----

(declare fits?)

(defn- comparison-fits? [[_op field _value]]
  ;; LHS must be a plain field name (string). Excludes count(...) LHS.
  (string? field))

(defn fits?
  "True iff the basic-mode editor can handle this AST.
  Currently: nil (empty), single (possibly negated) comparison, AND/OR groups
  of comparisons, nested up to MAX-DEPTH. LINKED TO and COUNT not yet."
  ([ast] (fits? ast 1))
  ([ast depth]
   (cond
     (nil? ast) true
     (count-node? ast) false
     (negated-comparison? ast) (comparison-fits? (second ast))
     (negated-cross-filter? ast) true
     (not-node? ast) false
     (comparison? ast) (comparison-fits? ast)
     (cross-filter? ast) true
     (group? ast) (and (<= depth MAX-DEPTH)
                       (seq (rest ast))
                       (every? #(fits? % (inc depth)) (rest ast)))
     :else false)))

;; ---- operators for UI ----

(defn- normalize-field-type [t]
  (case t
    "memo" "text"
    ("linkedlist" "multilist" "tags") "list"
    "date-time" "datetime"
    t))

(def ^:private all-types
  #{"text" "number" "list" "user" "date" "datetime"})

(def op-codes
  ;; Order is dropdown order
  [{:code "="
    :op "is"
    :negated? false
    :arity :binary
    :types all-types}
   {:code "≠"
    :op "is"
    :negated? true
    :arity :binary
    :types all-types}
   {:code "contains"
    :op "contains"
    :negated? false
    :arity :binary
    :types #{"text"}}
   {:code "does not contain"
    :op "contains"
    :negated? true
    :arity :binary
    :types #{"text"}}
   {:code "starts with"
    :op "starts-with"
    :negated? false
    :arity :binary
    :types #{"text"}}
   {:code "does not start with"
    :op "starts-with"
    :negated? true
    :arity :binary
    :types #{"text"}}
   {:code ">"
    :op "gt"
    :negated? false
    :arity :binary
    :types #{"number" "date" "datetime"}}
   {:code "≥"
    :op "gte"
    :negated? false
    :arity :binary
    :types #{"number" "date" "datetime"}}
   {:code "<"
    :op "lt"
    :negated? false
    :arity :binary
    :types #{"number" "date" "datetime"}}
   {:code "≤"
    :op "lte"
    :negated? false
    :arity :binary
    :types #{"number" "date" "datetime"}}
   {:code "in last"
    :op "in-last"
    :negated? false
    :arity :in-last
    :types #{"date" "datetime"}}
   {:code "not in last"
    :op "in-last"
    :negated? true
    :arity :in-last
    :types #{"date" "datetime"}}
   {:code "is empty"
    :op "empty"
    :negated? false
    :arity :unary
    :types all-types}
   ;; NOTE: not-empty is still a separate operator in AST
   {:code "is not empty"
    :op "not-empty"
    :negated? false
    :arity :unary
    :types all-types}])

(def ^:private op-by-code
  (into {} (map (juxt :code identity)) op-codes))

(def ^:private op-code-by-ast
  (into {}
        (map (fn [{:keys [code op negated?]}] [[op negated?] code]))
        op-codes))

(def ^:private all-op-codes (set (map :code op-codes)))

(defn op-arity [op-code]
  (:arity (op-by-code op-code)))

(defn valid-ops-for-type [field-type]
  (let [t (normalize-field-type field-type)]
    (if (contains? all-types t)
      (->> op-codes (filter #(contains? (:types %) t)) (map :code) set)
      all-op-codes)))

(defn clause-field-id [clause]
  (cond
    (or (cross-filter? clause) (negated-cross-filter? clause)) nil
    (not-node? clause) (clause-field-id (second clause))
    :else (second clause)))

(defn clause-value [clause]
  (cond
    (or (cross-filter? clause) (negated-cross-filter? clause)) nil
    (not-node? clause) (clause-value (second clause))
    :else (nth clause 2 nil)))

(defn cross-filter-ref [clause]
  (if (not-node? clause)
    (second (second clause))
    (second clause)))

(defn clause-op-code [clause]
  (when (or (comparison? clause) (negated-comparison? clause))
    (let [negated? (not-node? clause)
          inner (if negated? (second clause) clause)]
      (op-code-by-ast [(first inner) negated?]))))

(defn clause? [node]
  (or (comparison? node)
      (negated-comparison? node)
      (cross-filter? node)
      (negated-cross-filter? node)))

(defn assoc-clause-field-id [clause field-id]
  (cond
    (or (cross-filter? clause) (negated-cross-filter? clause)) clause
    (not-node? clause) (update clause 1 assoc-clause-field-id field-id)
    :else (assoc clause 1 field-id)))

(defn assoc-clause-value [clause value]
  (cond
    (or (cross-filter? clause) (negated-cross-filter? clause)) clause
    (not-node? clause) (update clause 1 assoc-clause-value value)
    :else (assoc clause 2 value)))

(defn wrap-as-group [ast]
  (cond
    (nil? ast) ["and"]
    (group? ast) ast
    :else ["and" ast]))

(defn negate-clause [clause]
  (if (not-node? clause)
    (second clause)
    ["not" clause]))

(defn display-name-fn-for
  "Build `(fn [field-id] -> display-name)` for an entity 
  from `fields-by-ptql-entity` sub data."
  [fields-by-entity entity]
  (let [by-id (into {}
                    (map (juxt :id :name))
                    (get fields-by-entity entity []))]
    (fn [field-id]
      (get by-id field-id field-id))))

(defn empty-clause []
  ["is" nil nil])

(defn empty-cross-filter []
  ["matches" {}])

(defn empty-group
  ([] (empty-group :and))
  ([combinator] [(name combinator) (empty-clause)]))

(defn build-clause [op-code field-id value]
  (let [{:keys [op negated? arity]} (op-by-code op-code)
        clause (if (= :unary arity)
                 [op field-id]
                 [op field-id value])]
    (if negated?
      ["not" clause]
      clause)))

(defn build-cross-filter [ref]
  ["matches" ref])

(defn change-op [clause new-op-code]
  (if (or (cross-filter? clause) (negated-cross-filter? clause))
    clause
    (let [old-arity (op-arity (clause-op-code clause))
          new-arity (op-arity new-op-code)]
      (build-clause new-op-code
                    (clause-field-id clause)
                    (cond
                      (= :unary new-arity) nil
                      (= old-arity new-arity) (clause-value clause)
                      (= :in-last new-arity) {:unit "days"}
                      :else nil)))))

(defn clause-complete? [clause]
  (cond
    (or (cross-filter? clause) (negated-cross-filter? clause))
    (some? (:view_id (cross-filter-ref clause)))

    :else
    (let [op-code (clause-op-code clause)
          arity (op-arity op-code)]
      (and (some? (clause-field-id clause))
           (case arity
             :unary true
             :binary (some? (clause-value clause))
             :in-last (let [v (clause-value clause)]
                        (and (map? v) (some? (:n v)) (not (str/blank? (:unit v))))))))))

(defn has-incomplete-clauses? [ast]
  (cond
    (nil? ast) false
    (group? ast) (boolean (some has-incomplete-clauses? (rest ast)))
    (clause? ast) (not (clause-complete? ast))
    :else false))

(defn valid-ast
  "Walk the working AST and produce the canonical AST for save/PTQL.
  Drops incomplete clauses, drops empty groups, collapses single-child groups.
  Returns nil if nothing valid is left."
  [ast]
  (cond
    (nil? ast)
    nil

    (group? ast)
    (let [kept (keep valid-ast (rest ast))]
      (case (count kept)
        0 nil
        1 (first kept)
        (vec (cons (first ast) kept))))

    (clause? ast)
    (when (clause-complete? ast) ast)

    :else nil))

(defn- path->indices [path]
  (mapv inc path))

(defn get-at [ast path]
  (get-in ast (path->indices path)))

(defn update-at [ast path f & args]
  (if (empty? path)
    (apply f ast args)
    (apply update-in ast (path->indices path) f args)))

(defn- vec-without [v i]
  (into (subvec v 0 i) (subvec v (inc i))))

(defn remove-at [ast path]
  (let [parent (vec (butlast path))
        i (last path)
        updated (update-at ast parent
                           (fn [[op & children]]
                             (into [op] (vec-without (vec children) i))))]
    (if (and (seq parent) (= 1 (count (get-at updated parent))))
      (remove-at updated parent)
      updated)))

(defn append-child [ast path child]
  (update-at ast path #(conj (vec %) child)))

(defn add-group-at [ast path]
  (let [[combinator & children] (get-at ast path)]
    (if (and (= path []) (seq children) (not (every? group? children)))
      ;; Root has direct clauses — wrap them into a group first, then add new group
      (update-at ast path (constantly [(name combinator) (into [(name combinator)] children) (empty-group)]))
      ;; Already in container mode (or nested) — just append
      (append-child ast path (empty-group)))))

(defn set-clause-field [ast path field-id field-type]
  (let [valid (valid-ops-for-type field-type)
        op-code (clause-op-code (get-at ast path))
        op-code (if (and op-code (valid op-code)) op-code "=")]
    (update-at ast path (constantly (build-clause op-code field-id nil)))))

;; ---- ast -> PTQL string ----

(defn- field-token [display-name]
  (if (and (re-matches #"[a-zA-Z_]\w*" display-name)
           (not (keywords (str/upper-case display-name))))
    display-name
    (str "'" display-name "'")))

(defn- emit-field [display-name-fn field-id]
  (field-token (display-name-fn field-id)))

(defn- emit-value [v]
  (cond
    (nil? v) ""
    (number? v) (str v)
    (map? v) (cond
               (= "current-user" (:fn v)) "CURRENT_USER"
               (= "group" (:fn v)) (str "GROUP \"" (:name v) "\"")
               :else (str v))
    :else (str "\"" v "\"")))

(declare emit-with-parent)

(defn- emit-comparison [display-name-fn [op field value]]
  (let [f (emit-field display-name-fn field)]
    (case op
      "is" (str f " = " (emit-value value))
      "contains" (str f " CONTAINS " (emit-value value))
      "starts-with" (str f " STARTS WITH " (emit-value value))
      "gt" (str f " > " (emit-value value))
      "gte" (str f " >= " (emit-value value))
      "lt" (str f " < " (emit-value value))
      "lte" (str f " <= " (emit-value value))
      "empty" (str f " IS EMPTY")
      "not-empty" (str f " IS NOT EMPTY")
      "in-last" (str f " IN LAST " (:n value) " " (:unit value)))))

(defn- emit-negated-comparison [display-name-fn [_ [op field value]]]
  (let [f (emit-field display-name-fn field)]
    (case op
      "is" (str f " != " (emit-value value))
      "contains" (str f " NOT CONTAINS " (emit-value value))
      "starts-with" (str f " NOT STARTS WITH " (emit-value value))
      "in-last" (str f " NOT IN LAST " (:n value) " " (:unit value)))))

(defn- emit-linked-to [display-name-fn linked-display-name-fn-for cross-filter-views [_ entity inner-ast] negated?]
  (let [prefix (if negated? "NOT LINKED TO " "LINKED TO ")
        inner-fn (linked-display-name-fn-for entity)]
    (cond-> (str prefix entity)
      inner-ast (str " WHERE ("
                     (emit-with-parent inner-fn linked-display-name-fn-for cross-filter-views inner-ast nil)
                     ")"))))

(defn- normalize-to-vector [v]
  (cond
    (sequential? v) v
    (some? v) [v]
    :else []))

(defn- emit-matches [linked-display-name-fn-for cross-filter-views view-ref]
  (let [view-id (:view_id view-ref)
        base (str "VIEW #" view-id)
        fields (normalize-to-vector (:field view-ref))
        values (normalize-to-vector (:auto_filter view-ref))
        pairs (->> (map vector fields values)
                   (remove (fn [[_ v]] (str/blank? v))))]
    (if (seq fields)
      (let [view-entity (get-in cross-filter-views [(str view-id) :entity])
            display-fn (linked-display-name-fn-for view-entity)]
        (str base " WHERE ("
             (->> pairs
                  (map (fn [[f v]]
                         (str (field-token (display-fn f)) " = "
                              (if (= "other_filter" v) "OTHER" (emit-value v)))))
                  (str/join " AND "))
             ")"))
      base)))

(defn- emit-group [display-name-fn linked-display-name-fn-for cross-filter-views [op & children] parent-op]
  (let [sep (case op "and" " AND " "or" " OR ")
        body (->> children
                  (map #(emit-with-parent display-name-fn linked-display-name-fn-for cross-filter-views % op))
                  (str/join sep))
        needs-parens? (or (and (= op "and") (= parent-op "or"))
                          (and (= op "or") (some? parent-op)))]
    (if needs-parens? (str "(" body ")") body)))

(defn- emit-with-parent [display-name-fn linked-display-name-fn-for cross-filter-views ast parent-op]
  (cond
    (nil? ast)
    ""

    (negated-comparison? ast)
    (emit-negated-comparison display-name-fn ast)

    (negated-cross-filter? ast)
    (str "NOT " (emit-matches linked-display-name-fn-for cross-filter-views (cross-filter-ref ast)))

    (negated-linked-to? ast)
    (emit-linked-to display-name-fn linked-display-name-fn-for cross-filter-views (second ast) true)

    (cross-filter? ast)
    (emit-matches linked-display-name-fn-for cross-filter-views (cross-filter-ref ast))

    (linked-to? ast)
    (emit-linked-to display-name-fn linked-display-name-fn-for cross-filter-views ast false)

    (comparison? ast)
    (emit-comparison display-name-fn ast)

    (group? ast)
    (emit-group display-name-fn linked-display-name-fn-for cross-filter-views ast parent-op)))

(defn ast->ptql
  "Emit PTQL from an AST that fits basic editor.
  
  Parameters:
  - `display-name-fn`: `(fn [field-id] -> display-name)` for source entity
  - `linked-display-name-fn`: `(fn [entity-name] -> display-name-fn)` for LINKED TO inner clauses
  - `cross-filter-views`: map keyed by view-id string -> {:name :entity} for matches ref entity lookup"
  [display-name-fn linked-display-name-fn-for cross-filter-views ast]
  (emit-with-parent display-name-fn linked-display-name-fn-for cross-filter-views ast nil))

;; ---- UI ----

(declare render-node)

(defn- in-last-value-row [path clause]
  (let [v (or (clause-value clause) {})]
    [:<>
     [:input.m-input.basic-clause-value-n
      {:type "number"
       :min 1
       :value (or (:n v) "")
       :on-change (fn [e]
                    (re-frame/dispatch [::events/set-clause-value path
                                        (assoc v :n (fu/try-parse-number (dom/event-value e)))]))}]
     [:select.m-input.basic-clause-value-unit
      {:value (or (:unit v) "days")
       :on-change (fn [e]
                    (re-frame/dispatch [::events/set-clause-value path
                                        (assoc v :unit (dom/event-value e))]))}
      (for [u ["days" "weeks" "months"]]
        ^{:key u} [:option {:value u} u])]]))

(defn- date-value-input [path clause time?]
  (let [v (clause-value clause)
        ;; PTQL stores datetime as "YYYY-MM-DD HH:MM:SS"
        ;; HTML datetime-local wants "YYYY-MM-DDTHH:MM[:SS]"
        input-value (cond
                      (str/blank? v) ""
                      (and time? (= 10 (count v))) (str v "T00:00")
                      time? (str/replace v " " "T")
                      :else v)]
    ;; TODO: use our custom date/datetime picker for consistency?
    [:input.m-input.basic-clause-value
     {:type (if time? "datetime-local" "date")
      :value input-value
      :on-change (fn [e]
                   (let [raw (dom/event-value e)
                         ptql-value (cond
                                      (str/blank? raw)
                                      nil

                                      ;; without seconds -> append :00
                                      (and time? (= 16 (count raw)))
                                      (str (str/replace raw "T" " ") ":00")

                                      time?
                                      (str/replace raw "T" " ")

                                      :else
                                      raw)]
                     (re-frame/dispatch [::events/set-clause-value path ptql-value])))}]))

(defn- value-select [path v items]
  [selector2/new-select
   {:items items
    :value (some #(when (= (:id %) v) %) items)
    :label-fn :name
    :key-fn :id
    :placeholder (get-translation "view-editor.basic.pick-value" "Pick a value")
    :on-change #(re-frame/dispatch [::events/set-clause-value path (:id %)])}])

(defn- clause-row [path clause]
  (let [fields-by-entity @(re-frame/subscribe [::subs/fields-by-ptql-entity])
        source-entity @(re-frame/subscribe [::view-editor.subs/entity-type])
        fields (sort-by :name (get fields-by-entity source-entity []))
        field-id (clause-field-id clause)
        selected-field (first (filter #(= (:id %) field-id) fields))
        field-type (:type selected-field)
        op-code (clause-op-code clause)
        arity (op-arity op-code)
        field-values (if (= "tags" field-type)
                       (mapv first @(re-frame/subscribe [::subs/tags]))
                       (:values selected-field))
        valid-ops (valid-ops-for-type field-type)
        ;; NOTE: for cases when invalid op was already in the view
        visible-ops (if (and op-code (not (valid-ops op-code)))
                      (conj valid-ops op-code)
                      valid-ops)]
    [:div.basic-clause-row
     [selector2/new-select
      {:items fields
       :value selected-field
       :label-fn :name
       :key-fn :id
       :placeholder (get-translation "view-editor.basic.pick-field" "Pick a field")
       :on-change #(re-frame/dispatch [::events/set-clause-field path (:id %) (:type %)])}]
     [:select.m-input.basic-clause-op
      {:value (or op-code "")
       :on-change #(re-frame/dispatch [::events/set-clause-op path (dom/event-value %)])}
      (for [{:keys [code]} op-codes
            :when (visible-ops code)]
        ^{:key code} [:option {:value code} code])]
     [:div.basic-clause-value-cell
      (case arity
        :in-last [in-last-value-row path clause]
        :binary (let [v (clause-value clause)]
                  (cond
                    (= "date" field-type)
                    [date-value-input path clause false]

                    (#{"date-time" "datetime"} field-type)
                    [date-value-input path clause true]

                    (= "user" field-type)
                    (let [users @(re-frame/subscribe [::subs/users])
                          groups @(re-frame/subscribe [::subs/groups])
                          supports-groups? (= field-id "assigned_to")
                          items (vec (concat
                                       [{:id {:fn "current-user"} :name "Current user"}]
                                       (map (fn [u]
                                              {:id (:email u)
                                               :name (:name u)})
                                            users)
                                       (when supports-groups?
                                         (map (fn [g]
                                                {:id {:fn "group" :name (:name g)}
                                                 :name (str "Group: " (:name g))})
                                              groups))))]
                      [value-select path v items])

                    (#{"milestone" "linked_milestones"} field-type)
                    ;; possible-values are [id name] pairs: show the name, store the id (PTQL filters by id)
                    [value-select path (str v) (mapv (fn [[id name]] {:id (str id) :name name}) field-values)]

                    (= "linkedlist" field-type)
                    [value-select path v (mapv (fn [val] {:id val :name val})
                                               (->> field-values (mapcat second) (distinct) (vec)))]

                    (seq field-values)
                    [value-select path v (mapv (fn [val] {:id val :name val}) field-values)]

                    :else
                    [:input.m-input.basic-clause-value
                     {:type "text"
                      :value (or v "")
                      :on-change #(re-frame/dispatch [::events/set-clause-value path (dom/event-value %)])}]))
        nil)]
     [buttons/icon-button
      {:icon [icons/mux-x]
       :tooltip (get-translation "view-editor.basic.remove" "Remove")
       :on-click #(re-frame/dispatch [::events/remove-at path])}]]))

(defn- cross-filter-row [path clause]
  (let [modules @(re-frame/subscribe [::view-editor.subs/cross-filter-modules])
        urls @(re-frame/subscribe [::view-editor.subs/cross-filter-urls])
        views @(re-frame/subscribe [::view-editor.subs/cross-filter-views])
        entity @(re-frame/subscribe [::view-editor.subs/cross-filter-entity-at-path path])
        negated? (not-node? clause)
        view-id (:view_id (cross-filter-ref clause))
        view-name (get-in views [(str view-id) :name])]
    [:div.basic-clause-row.basic-cross-filter-row
     ;; entity
     [:div.input-group.basic-cross-filter-entity-cell
      [:span.input-group-text.basic-cross-filter-link-icon [icons/mux-link2]]
      [:select.form-control.basic-cross-filter-entity
       {:value entity
        :on-change #(re-frame/dispatch [::events/set-cross-filter-entity path (dom/event-value %)])}
       (for [m modules]
         ^{:key m} [:option {:value m} m])]]
     ;; is/is not
     [:select.m-input.basic-clause-op
      {:value (if negated? "is not" "is")
       :on-change #(re-frame/dispatch [::events/toggle-cross-filter-negation path])}
      [:option {:value "is"} "is"]
      [:option {:value "is not"} "is not"]]
     ;; picker
     [:div.basic-clause-value-cell.basic-cross-filter-picker
      [application-provider
       ;; XXX: should we use custom CSS class?
       [:> pt-ts-bridge/GenericSearchableTree
        {:scope (clj->js {:userId (str (:id @(re-frame/subscribe [::subs/current-user])))
                          :projectId (str @(re-frame/subscribe [::subs/current-project-id]))
                          :module entity
                          :entity entity
                          :tab "filters"})
         :urls (clj->js (get urls entity))
         :initialSelectedItem (when view-id #js {:id (str view-id) :name (or view-name "")})
         :selectedValue (when view-id (str view-id))
         :onChange (fn [server-obj]
                     (re-frame/dispatch
                       [::events/set-cross-filter-view path (js->clj server-obj :keywordize-keys true)]))}]]]
     [buttons/icon-button
      {:icon [icons/mux-x]
       :tooltip (get-translation "view-editor.basic.remove" "Remove")
       :on-click #(re-frame/dispatch [::events/remove-at path])}]]))

(defn- group-actions [path can-add-nested?]
  (let [modules @(re-frame/subscribe [::view-editor.subs/cross-filter-modules])]
    [:div.basic-group-actions
     [buttons/button
      {:tertiary? true
       :icon [icons/mux-reg-plus]
       :text (get-translation "view-editor.basic.add-condition" "Condition")
       :on-click #(re-frame/dispatch [::events/add-clause path])}]
     (when (seq modules)
       [buttons/button
        {:tertiary? true
         :icon [icons/mux-reg-plus]
         :text (get-translation "view-editor.basic.add-cross-filter" "Cross Filter Condition")
         :on-click #(re-frame/dispatch [::events/add-cross-filter path])}])
     [buttons/button
      {:tertiary? true
       :disabled? (not can-add-nested?)
       :icon [icons/mux-reg-plus]
       :text (get-translation "view-editor.basic.add-nested-group" "Nested Group Condition")
       :on-click (when can-add-nested?
                   #(re-frame/dispatch [::events/add-nested-group path]))}]]))

(defn- combinator-select [path combinator]
  [:select.form-select.basic-group-combinator
   {:value     combinator
    :on-change #(re-frame/dispatch [::events/set-group-combinator path (keyword (dom/event-value %))])}
   [:option {:value "and"} (get-translation "view-editor.basic.and" "AND")]
   [:option {:value "or"} (get-translation "view-editor.basic.or" "OR")]])

(defn- group-card [path combinator children show-remove? child-count show-actions? can-add-nested?]
  (let [multi? (> child-count 1)]
    [:div.basic-group-card {:class (when (and multi? (= "or" combinator)) "is-or")}
     (when (or show-remove? multi?)
       [:div.basic-group-card-header
        (when multi?
          [:<>
           [:span.basic-group-card-header-label
            (get-translation "generic.button.apply" "Apply")]
           [combinator-select path combinator]
           [:span.basic-group-card-header-label
            (get-translation "view-editor.basic.between-the-following" "between the following")]])
        (when show-remove?
          [buttons/icon-button
           {:icon [icons/mux-x]
            :tooltip (get-translation "view-editor.basic.remove-group" "Remove group")
            :on-click #(re-frame/dispatch [::events/remove-at path])}])])
     [:div.basic-group-card-body
      children]
     (when show-actions?
       [group-actions path can-add-nested?])]))

(defn- group-section [path group can-add-nested?]
  (let [combinator (first group)
        children (rest group)
        root? (= path [])
        all-group-children? (and (seq children) (every? group? children))
        show-actions? (< (count path) MAX-DEPTH)]
    (if (and root? all-group-children?)
      [:div.basic-root-container
       (map-indexed (fn [i child]
                      ^{:key i}
                      [:div
                       (when (pos? i)
                         [:div.basic-group-separator
                          {:class (when (= "or" combinator) "is-or")}
                          [combinator-select path combinator]])
                       [render-node (conj path i) child true]])
                    children)
       [:div.basic-add-group-outer
        [buttons/button
         {:tertiary? true
          :icon [icons/mux-reg-plus]
          :text (get-translation "view-editor.basic.add-group" "Add Group")
          :on-click #(re-frame/dispatch [::events/add-group path])}]]]
      ;; Root with only clauses, or non-root group: render as a card.
      ;; Card-mode children are nested sub-groups — they cannot add more nesting.
      [:div.basic-filter-frame
       [group-card path combinator
        (map-indexed (fn [i child]
                       ^{:key i} [render-node (conj path i) child false])
                     children)
        (and (not root?) (> (count children) 1))
        (count children)
        show-actions?
        can-add-nested?]
       (when (and root? (seq children))
         [:div.basic-add-group-outer
          [buttons/button
           {:tertiary? true
            :icon [icons/mux-reg-plus]
            :text (get-translation "view-editor.basic.add-group" "Add Group")
            :on-click #(re-frame/dispatch [::events/add-group path])}]])])))

(defn render-node [path node can-add-nested?]
  (cond
    (or (cross-filter? node) (negated-cross-filter? node)) [cross-filter-row path node]
    (clause? node) [clause-row path node]
    (group? node) [group-section path node can-add-nested?]))

(defn basic-filter-section [_form]
  (let [ast @(re-frame/subscribe [::view-editor.subs/basic-builder-ast])]
    [render-node [] ast true]))

(comment

  (fits? ["not" ["is" "priority" "High"]])
  (fits? ["not" ["exists-related" "Test" nil]])
  (fits? ["not" ["and" ["is" "priority" "High"]]])
  (fits? ["count" ["exists-related" "Test" nil]])

  (build-clause "not in last" "created_at" {"n" 7 "unit" "days"})
  (change-op ["is" "priority" "High"] "is empty")
  (change-op ["is" "priority" "High"] "≠")

  (valid-ast nil)
  (valid-ast ["and"])
  (valid-ast ["and" ["is" nil nil]])
  (valid-ast ["and" ["is" "priority" "High"]])
  (valid-ast ["and" ["is" "priority" "High"]
              ["or" ["is" "status" "Ready"] ["is" "status" "Open"]]])
  (valid-ast ["and" ["is" "priority" "High"]
              ["or" ["is" "status" "Ready"] ["is" "status" nil]]])

  (change-op ["is" "field" "value"] "in last")
  (change-op ["in-last" "field" {"n" 7 "unit" "days"}] "not in last")
  (change-op ["empty" "field"] "in last")

  @(re-frame.core/subscribe [:practitest.frontend.subs/fields-by-ptql-entity])

  (re-frame.core/clear-subscription-cache!))
